import React from 'react';
import Modal from '../Modal';
import { Remorque } from '../../types';
import { TruckIcon, CalendarIcon, CubeIcon } from '@heroicons/react/24/outline';

interface TrailerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  trailer: Remorque;
}

const TrailerDetailsModal: React.FC<TrailerDetailsModalProps> = ({ isOpen, onClose, trailer }) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Non disponible';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateVolume = () => {
    if (trailer.longueur && trailer.largeur && trailer.hauteur) {
      return (trailer.longueur * trailer.largeur * trailer.hauteur).toFixed(2);
    }
    return 'Non calculable';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails de la remorque" size="lg">
      <div className="space-y-6">
        {/* En-tête avec icône */}
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <TruckIcon className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {trailer.immatriculation}
            </h2>
            <p className="text-lg text-gray-600">
              {trailer.marque} {trailer.modele || ''}
            </p>
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Informations générales
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Immatriculation:</span>
                <span className="text-gray-900 font-mono">{trailer.immatriculation}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Marque:</span>
                <span className="text-gray-900">{trailer.marque}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Modèle:</span>
                <span className="text-gray-900">{trailer.modele || 'Non défini'}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Capacité:</span>
                <span className="text-gray-900">{trailer.capacite ? `${trailer.capacite} kg` : 'Non définie'}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Propriétaire:</span>
                <span className="text-gray-900">{trailer.proprietaire || 'Non défini'}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Dimensions et caractéristiques
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Longueur:</span>
                <span className="text-gray-900">{trailer.longueur ? `${trailer.longueur} m` : 'Non définie'}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Largeur:</span>
                <span className="text-gray-900">{trailer.largeur ? `${trailer.largeur} m` : 'Non définie'}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Hauteur:</span>
                <span className="text-gray-900">{trailer.hauteur ? `${trailer.hauteur} m` : 'Non définie'}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Volume calculé:</span>
                <span className="text-gray-900">{calculateVolume()} m³</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Tare théorique:</span>
                <span className="text-gray-900">
                  {trailer.tare_theorique ? `${trailer.tare_theorique} kg` : 'Non définie'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Statut et notes */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <CubeIcon className="w-5 h-5 mr-2 text-gray-600" />
            État et informations
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Statut:</span>
              <p className="text-gray-900 capitalize">{trailer.statut}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Notes:</span>
              <p className="text-gray-900">{trailer.notes || 'Aucune note'}</p>
            </div>
          </div>
        </div>

        {/* Informations de dates */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <CalendarIcon className="w-5 h-5 mr-2 text-gray-600" />
            Informations temporelles
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Créé le:</span>
              <p className="text-gray-900">{formatDate(trailer.created_at)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Modifié le:</span>
              <p className="text-gray-900">{formatDate(trailer.updated_at)}</p>
            </div>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TrailerDetailsModal;
